import z from 'zod';

import { AGE, GENDER, RACE, SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { DiscoveryAssistantMode, DiscoveryUserMode } from './discovery-constants';

export const errorResponseSchema = z.object({
  message: z.string(),
  code: z.string().nullable().optional(),
});

export const demographicsSchema = z
  .object({
    age: z.array(z.enum(AGE)).nullable().optional(),
    gender: z.array(z.enum(GENDER)).nullable().optional(),
    race: z.array(z.enum(RACE)).nullable().optional(),
    location: z.array(z.string()).nullable().optional(),
    language: z.array(z.string()).nullable().optional(),
  })
  .nullable()
  .optional();

export const engagementSchema = z
  .object({
    followers_min: z.number().int().nullable().optional(),
    followers_max: z.number().int().nullable().optional(),
    er_min: z.number().nullable().optional(),
    er_max: z.number().nullable().optional(),
  })
  .nullable()
  .optional();

export const filterConditionsSchema = z.object({
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  platform: z.array(z.enum(SOURCE_PLATFORM_TYPE)).nullable().optional(),
  demographics: demographicsSchema,
  engagement: engagementSchema,
  official_account_only: z.boolean().nullable().optional(),
});

export const searchConditionsSchema = z.object({
  filter_conditions: filterConditionsSchema,
  post_feature_conditions: z.array(z.string()).optional(),
});

export const userPromptContentSchema = z.object({
  message: z.string(),
});

export const userCommoanalityPostsListItemSchema = z.object({
  post_id: z.string(),
  poster_url: z.url(),
});

export const userPostsContentSchema = z.object({
  post_list: z.array(userCommoanalityPostsListItemSchema),
  filter_conditions: filterConditionsSchema,
});

export const searchModeContentSchema = z.object({
  message: z.string(),
  search_conditions: searchConditionsSchema,
});

export const suggestionModeContentSchema = z.object({
  message: z.string(),
  suggested_prompts: z.array(z.string()),
});

export const answerModeContentSchema = z.object({
  message: z.string(),
});

export const failureModeContentSchema = z.object({
  message: z.string(),
  failure_reason: z.string(),
});

export const keywordSelectionModeContentSchema = z.object({
  message: z.string(),
  filter_conditions: filterConditionsSchema,
  candidate_post_feature_conditions: z.array(z.string()).min(1).max(10),
  selected_post_feature_conditions: z.array(z.string()).nullable().optional(),
});

export const userPromptMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('user'),
  user_mode: z.literal(DiscoveryUserMode.PROMPT),
  assistant_mode: z.null().optional(),
  content: userPromptContentSchema,
  created_at: z.string(),
});

export const userPostsMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('user'),
  user_mode: z.literal(DiscoveryUserMode.POSTS),
  assistant_mode: z.null().optional(),
  content: userPostsContentSchema,
  created_at: z.string(),
});

export const searchModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.SEARCH),
  user_mode: z.null().optional(),
  content: searchModeContentSchema,
  search_history_id: z.string().nullable(),
  created_at: z.string(),
  keyword_selection_message_id: z.string().optional(),
});

export const suggestionModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.SUGGESTION),
  user_mode: z.null().optional(),
  content: suggestionModeContentSchema,
  created_at: z.string(),
});

export const failureModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.FAILURE),
  user_mode: z.null().optional(),
  content: failureModeContentSchema,
  created_at: z.string(),
});

export const answerModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.ANSWER),
  user_mode: z.null().optional(),
  content: answerModeContentSchema,
  created_at: z.string(),
});

export const keywordSelectionModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.KEYWORD_SELECTION),
  user_mode: z.null().optional(),
  content: keywordSelectionModeContentSchema,
  created_at: z.string(),
});

export const loadingModeMessageResponseSchema = z.object({
  message_id: z.string(),
  discovery_session_id: z.string(),
  role: z.literal('assistant'),
  assistant_mode: z.literal(DiscoveryAssistantMode.LOADING),
  user_mode: z.null().optional(),
  content: answerModeContentSchema,
  created_at: z.string(),
});

export const assistantMessageResponseSchema = z.union([
  searchModeMessageResponseSchema,
  suggestionModeMessageResponseSchema,
  failureModeMessageResponseSchema,
  answerModeMessageResponseSchema,
  keywordSelectionModeMessageResponseSchema,
]);

export const discoveryMessageSchema = z.union([
  userPromptMessageResponseSchema,
  userPostsMessageResponseSchema,
  searchModeMessageResponseSchema,
  suggestionModeMessageResponseSchema,
  failureModeMessageResponseSchema,
  answerModeMessageResponseSchema,
  keywordSelectionModeMessageResponseSchema,
  loadingModeMessageResponseSchema,
]);

export const CreatorContentSchema = z.object({
  id: z.string(),
  poster_url: z.string(),
  video_url: z.string(),
  source_url: z.string(),
  content: z.string(),
  ai_matched_features: z.array(z.string()).optional().nullable(),
  engagement: z.object({
    view: z.number(),
    like: z.number(),
    comment: z.number(),
    rate: z.number(),
    shared: z.number(),
  }),
  date: z.string(),
});

export const CreatorSchema = z.object({
  creator_id: z.string(),
  search_id: z.string(),
  post_categories: z.array(z.string()),
  is_saved: z.boolean(),
  saved_at: z.string().optional(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  username: z.string(),
  nickname: z.string(),
  profile_image_url: z.string(),
  followers: z.number(),
  average_er: z.number(),
  matched_keywords: z.array(z.string()),
  matched_posts: z.array(CreatorContentSchema).optional(),
  matched_post_ids: z.array(z.string()).optional(),
  excluded_date: z.string(),
});

export type Engagement = z.infer<typeof engagementSchema>;
export type Demographic = z.infer<typeof demographicsSchema>;

export type ErrorResponse = z.infer<typeof errorResponseSchema>;
export type FilterConditions = z.infer<typeof filterConditionsSchema>;
export type SearchConditions = z.infer<typeof searchConditionsSchema>;
export type DiscoveryMessage = z.infer<typeof discoveryMessageSchema>;
export type SearchModeMessageResponse = z.infer<typeof searchModeMessageResponseSchema>;

export type UserCommonalityPostListItem = z.infer<typeof userCommoanalityPostsListItemSchema>;

export type UserPromptMessage = z.infer<typeof userPromptMessageResponseSchema>;
export type UserPostsMessage = z.infer<typeof userPostsMessageResponseSchema>;
export type AssistantSearchMessage = z.infer<typeof searchModeMessageResponseSchema>;
export type AssistantSuggestionMessage = z.infer<typeof suggestionModeMessageResponseSchema>;
export type AssistantFailureMessage = z.infer<typeof failureModeMessageResponseSchema>;
export type AssistantAnswerMessage = z.infer<typeof answerModeMessageResponseSchema>;
export type AssistantKeywordSelectionMessage = z.infer<
  typeof keywordSelectionModeMessageResponseSchema
>;
export type AssistantLoadingMessage = z.infer<typeof loadingModeMessageResponseSchema>;

export type UserPromptContent = z.infer<typeof userPromptContentSchema>;
export type UserPostsContent = z.infer<typeof userPostsContentSchema>;
export type AssistantSearchContent = z.infer<typeof searchModeContentSchema>;
export type AssistantSuggestionContent = z.infer<typeof suggestionModeContentSchema>;
export type AssistantAnswerContent = z.infer<typeof answerModeContentSchema>;
export type AssistantFailureContent = z.infer<typeof failureModeContentSchema>;
export type AssistantKeywordSelectionContent = z.infer<typeof keywordSelectionModeContentSchema>;
export type CreatorContent = z.infer<typeof CreatorContentSchema>;
export type Creator = z.infer<typeof CreatorSchema>;
